import { useState } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useRatings } from '@/hooks/useRatings';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface RatingStarsProps {
  figurineId: string;
  avgRating: number;
  ratingCount: number;
  size?: 'sm' | 'md';
  interactive?: boolean;
  onAuthRequired?: () => void;
}

export default function RatingStars({
  figurineId,
  avgRating,
  ratingCount,
  size = 'md',
  interactive = true,
  onAuthRequired,
}: RatingStarsProps) {
  const { user } = useAuth();
  const { userRating, submitting, rate } = useRatings(figurineId);
  const [hovered, setHovered] = useState<number | null>(null);

  const starSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  const displayRating = hovered ?? userRating ?? avgRating;

  const handleClick = async (stars: number) => {
    if (!user) {
      onAuthRequired?.();
      toast.info('Zaloguj się, aby oceniać figurki');
      return;
    }
    const { error } = await rate(stars);
    if (error) toast.error(error);
    else toast.success(`Oceniłeś na ${stars} ${stars === 1 ? 'gwiazdkę' : 'gwiazdki'}!`);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = star <= displayRating;
          return (
            <motion.button
              key={star}
              disabled={submitting || !interactive}
              onClick={() => interactive && handleClick(star)}
              onMouseEnter={() => interactive && user && setHovered(star)}
              onMouseLeave={() => interactive && setHovered(null)}
              whileHover={interactive ? { scale: 1.2 } : {}}
              whileTap={interactive ? { scale: 0.9 } : {}}
              className="focus:outline-none disabled:cursor-default"
              aria-label={`Ocen ${star} gwiazdek`}
            >
              <Star
                className={starSize}
                style={{
                  fill: filled ? 'var(--chart-4)' : 'transparent',
                  color: filled ? 'var(--chart-4)' : 'var(--muted-foreground)',
                  filter: filled ? 'drop-shadow(0 0 4px oklch(0.72 0.24 45 / 0.6))' : 'none',
                  transition: 'all 0.15s ease',
                }}
              />
            </motion.button>
          );
        })}
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="text-xs font-bold"
          style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--chart-4)' }}
        >
          {avgRating > 0 ? avgRating.toFixed(1) : '—'}
        </span>
        <span className="text-xs text-muted-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          ({ratingCount} {ratingCount === 1 ? 'ocena' : ratingCount < 5 ? 'oceny' : 'ocen'})
        </span>
        {userRating && (
          <span className="text-xs text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            · Twoja: {userRating}★
          </span>
        )}
      </div>
    </div>
  );
}
